import { Tabs } from 'antd'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ContentContainer } from '../../components/custom_components/ContentContainer'
import { LoadingAndErrors } from '../../components/custom_components/LoadingAndErrors'
import { PerTabBarContent } from '../../components/custom_components/TabBarContent'
import { useAuth } from '../../context/auth-context'
import useReplyerMeetingsAndRequests from '../../graphql/meeting/hooks/useReplyerMeetingsAndRequests'
import { useCollapse } from '../../routes/collapse-context'
import { dateSorter } from '../../shared/sortUtils'
import { useDateTime } from '../../shared/useDateTime'
import { MeetingDashboardFragment, MeetingHrFeedbackResult, MeetingStatus, MeetingType, RoleName } from '../../types'
import { EmployeeTargetsContainer } from '../targets/EmployeeTargetsContainer'
import { EmployeeMeetingStatusBox } from './EmployeeMeetingStatusBox'
import { OwnGrowthEvaluationContainer } from './OwnGrowthEvaluationContainer'
import { CurrencyFromContractProvider } from '../../context/currency-context'
import { MeetingRequestOptionsProvider } from '../../context/meeting-request-options-context'

const { TabPane } = Tabs

export const OwnGrowthPage: FC = () => {
  const [t] = useTranslation()
  const [activeTabKey, setActiveTabKey] = useState('1')

  const { user } = useAuth()
  const { utcDateStringToMoment } = useDateTime(user)
  const { data, error, loading: meetingsLoading } = useReplyerMeetingsAndRequests(user.employeeId, false, true)
  const collapsed = useCollapse()

  const isCompanyOrClusterOrNetworkManager = (userRoles: RoleName[] | undefined | null): boolean => {
    if (!userRoles || userRoles.length === 0) {
      return false
    }
    return userRoles.some((role) => [RoleName.CompanyManager, RoleName.ClusterManager, RoleName.NetworkManager].includes(role))
  }

  if (
    !user.roles ||
    user.roles.length === 0 ||
    isCompanyOrClusterOrNetworkManager(user.roles) ||
    meetingsLoading ||
    error ||
    !data ||
    !data.meetingsByReplyer
  ) {
    return (
      <ContentContainer collapsed={collapsed}>
        <LoadingAndErrors
          isLoading={meetingsLoading}
          hasNoData={!data || !data.meetingsByReplyer}
          isPermissionDenied={!error && (!user.roles || user.roles.length === 0 || isCompanyOrClusterOrNetworkManager(user.roles))}
          error={error}
        />
      </ContentContainer>
    )
  }

  const meetingData = data.meetingsByReplyer

  // All meetings that are Canceled or Disputed but with HR feedback are excluded
  const sortMeetingData = [...meetingData]
    .filter((meeting) => meeting.status !== MeetingStatus.Canceled)
    .filter((meeting) => [MeetingType.Appraisal, MeetingType.AppraisalUpdate].includes(meeting.type))
    .filter((meeting) => {
      return !(
        meeting.status === MeetingStatus.DisputedConfirmed &&
        meeting.appraisal &&
        meeting.appraisal.hrResult !== MeetingHrFeedbackResult.NoResult
      )
    })
    .sort((a, b) => dateSorter(utcDateStringToMoment(a.displayDate.valueOf()), utcDateStringToMoment(b.displayDate.valueOf())))

  const isMeetingScheduled: boolean = sortMeetingData.length > 0
  const latestMeeting: MeetingDashboardFragment | undefined = isMeetingScheduled ? sortMeetingData[0] : undefined

  const renderTabBar = () => (
    <PerTabBarContent
      tabs={
        <Tabs onChange={setActiveTabKey}>
          <TabPane key={'1'} tab={t('ownGrowthView.tabs.targets')} />
          <TabPane key={'2'} tab={t('ownGrowthView.tabs.evaluation')} />
        </Tabs>
      }
    />
  )

  return (
    <CurrencyFromContractProvider employeeId={user.employeeId}>
      <MeetingRequestOptionsProvider employeeId={user.employeeId}>
        <ContentContainer collapsed={collapsed}>
          <Tabs activeKey={activeTabKey} renderTabBar={renderTabBar} className="full-width">
            <TabPane key="1">
              {latestMeeting && <EmployeeMeetingStatusBox meeting={latestMeeting} />}
              <EmployeeTargetsContainer plannedMeeting={latestMeeting} view="OwnGrowthPage" employeeSkillStatus={null} />
            </TabPane>
            <TabPane key="2">
              <OwnGrowthEvaluationContainer
                plannedMeeting={latestMeeting}
                employeeEditable
                view="OwnGrowthPage"
                employeeSkillStatus={null}
              />
            </TabPane>
          </Tabs>
        </ContentContainer>
      </MeetingRequestOptionsProvider>
    </CurrencyFromContractProvider>
  )
}
